/**
 * 全部用户列表（超级管理员专属）
 *
 * 与 A5「用户查询」(/users) 互补：查询页需要先输入关键词，本页直接分页浏览
 * 全量注册用户，按注册时间倒序，便于超管掌握用户规模与最新注册情况。
 *
 * 权限：仅 super_admin 可见（菜单项亦仅对超管渲染）。其余角色直接 403 fallback。
 *   后端 GET /users/list 同样二次校验 role === 'super_admin'，前端隐藏只是体验层。
 *
 * PII：列表 phone（L2）/ unionId（L3）始终脱敏，需明文请点 userId 进单用户详情页。
 */
import { useState } from 'react';
import { Card, Table, Tag, Alert, Result, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../../../stores/session';
import { listAllUsers, type UserSearchItem, type UserStatus } from '../../../api/users';

const { Text } = Typography;

function userStatusColor(status: UserStatus): string {
  switch (status) {
    case 'active':    return 'green';
    case 'frozen':    return 'red';
    case 'recycling': return 'gold';
    case 'deleted':   return 'default';
    default:          return 'default';
  }
}

function userStatusLabel(status: UserStatus): string {
  switch (status) {
    case 'active':    return '正常';
    case 'frozen':    return '已冻结';
    case 'recycling': return '回收中';
    case 'deleted':   return '已删除';
    default:          return status;
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function UsersListAllInner() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['users-list-all', page, pageSize],
    queryFn: () => listAllUsers({ page, pageSize }),
  });

  const columns: ColumnsType<UserSearchItem> = [
    {
      title: '用户 ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (userId: string) => (
        <Link to={`/users/${encodeURIComponent(userId)}`} data-testid={`user-link-${userId}`}>
          {userId}
        </Link>
      ),
    },
    { title: '昵称', dataIndex: 'nickname', key: 'nickname', width: 140 },
    { title: '手机号（脱敏）', dataIndex: 'phoneMasked', key: 'phoneMasked', width: 140 },
    { title: 'unionId（脱敏）', dataIndex: 'unionIdMasked', key: 'unionIdMasked', width: 180 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: UserStatus) => (
        <Tag color={userStatusColor(s)} data-testid={`status-tag-${s}`}>
          {userStatusLabel(s)}
        </Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <div data-testid="users-list-all-page">
      <Card
        title={`全部用户${query.data ? `（共 ${query.data.total} 人）` : ''}`}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="本页仅超级管理员可见。手机号 / unionId 已脱敏，需查看明文请点击用户 ID 进入详情页。"
          data-testid="users-list-all-hint"
        />

        <Table<UserSearchItem>
          rowKey="userId"
          columns={columns}
          dataSource={query.data?.items ?? []}
          loading={query.isLoading}
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          data-testid="users-list-all-table"
        />

        {query.isError && (
          <Text type="danger" data-testid="users-list-all-error">
            加载失败，请稍后重试。
          </Text>
        )}
      </Card>
    </div>
  );
}

export default function UsersListAllPage() {
  const role = useSession((s) => s.role);
  if (role !== 'super_admin') {
    return (
      <Result
        status="403"
        title="无权访问"
        subTitle="全部用户列表仅超级管理员可见。"
        data-testid="users-list-all-403"
      />
    );
  }
  return <UsersListAllInner />;
}
